import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { fetchExams, startAttempt } from '../mishtameretPortalApi';
import { DEFAULT_EXAM_FRAME, EXAM_FRAMES, examsPath } from '../nav';
import ArchiveExamSearch from '../components/ArchiveExamSearch';
import GemaraOrderNotice from '../components/GemaraOrderNotice';
import EmailPromptNotice from '../components/EmailPromptNotice';

const ExamsScreen = () => {
  const { theme, member, onMemberChange, isStation = false, basePath = '/mishtameret' } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const frameId = searchParams.get('frame') || DEFAULT_EXAM_FRAME;
  const frameMeta = EXAM_FRAMES.find((f) => f.id === frameId);

  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingId, setStartingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchExams();
        if (!cancelled) setFrames(data.frames || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת מבחנים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeFrame = useMemo(
    () => frames.find((f) => f.id === frameId) || null,
    [frames, frameId]
  );

  if (!frameMeta) {
    return <Navigate to={examsPath(DEFAULT_EXAM_FRAME, isStation)} replace />;
  }

  const handleStart = async (examId) => {
    setStartingId(examId);
    setError('');
    try {
      const attempt = await startAttempt(examId);
      navigate(`${basePath}/exams/take/${attempt.id}`);
    } catch (err) {
      // מבחן שכבר הושלם — השרת מחזיר את הניסיון, ומסך המבחן מציג אותו כסקירה עם התשובות
      const doneAttempt = err.response?.data?.attempt;
      if (doneAttempt?.id) {
        navigate(`${basePath}/exams/take/${doneAttempt.id}`);
        return;
      }
      setError(err.response?.data?.error || 'לא ניתן להתחיל את המבחן');
    } finally {
      setStartingId(null);
    }
  };

  const actionLabel = (exam) => {
    if (exam.status === 'completed') return 'צפייה בתשובות';
    if (exam.status === 'in_progress') return 'המשך מבחן';
    return exam.ready ? 'התחלת מבחן' : 'לא מוכן';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  const exams = activeFrame?.exams || [];

  return (
    <Box>
      <GemaraOrderNotice frame={frameId} theme={theme} />
      {isStation ? null : <EmailPromptNotice theme={theme} member={member} onMemberChange={onMemberChange} />}
      {error ? (
        <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography>
      ) : null}

      {!exams.length ? (
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.radius}px`,
            border: `1px dashed ${theme.border}`,
            color: theme.textMuted,
            textAlign: 'center',
          }}
        >
          אין כרגע מבחנים פעילים במסגרת זו
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {exams.map((exam) => {
            const done = exam.status === 'completed';
            return (
            <Box
              key={exam.id}
              sx={{
                p: 2,
                borderRadius: `${theme.radius}px`,
                border: `1px solid ${done ? theme.success : theme.borderSoft}`,
                bgcolor: theme.surface,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{exam.title}</Typography>
                {exam.description ? (
                  <Typography sx={{ color: theme.textMuted, mt: 0.35, fontSize: '0.95rem' }}>
                    {exam.description}
                  </Typography>
                ) : null}
                <Typography sx={{ color: theme.textMuted, mt: 0.5, fontSize: '0.85rem' }}>
                  {exam.questionCount} שאלות
                </Typography>
                {done ? (
                  <Typography sx={{ color: theme.success, fontWeight: 700, mt: 0.5, fontSize: '0.95rem' }}>
                    ✓ נבחנתם · ציון {exam.score ?? 0}
                  </Typography>
                ) : null}
                {exam.status === 'in_progress' ? (
                  <Typography sx={{ color: theme.accent, fontWeight: 700, mt: 0.5, fontSize: '0.95rem' }}>
                    התחלתם ועדיין לא סיימתם
                  </Typography>
                ) : null}
              </Box>
              <Button
                disabled={(!done && !exam.ready) || startingId === exam.id}
                onClick={() => handleStart(exam.id)}
                sx={{
                  minHeight: 44,
                  px: 2.5,
                  borderRadius: 999,
                  bgcolor: done ? 'transparent' : theme.accent,
                  border: done ? `1px solid ${theme.border}` : 'none',
                  color: done ? theme.text : theme.accentContrast,
                  fontWeight: 700,
                  flexShrink: 0,
                  '&:hover': { bgcolor: done ? theme.surfaceHover : theme.accentSoft },
                  '&.Mui-disabled': { bgcolor: theme.borderSoft, color: theme.textMuted },
                }}
              >
                {startingId === exam.id ? (
                  <CircularProgress size={22} sx={{ color: done ? theme.text : theme.accentContrast }} />
                ) : actionLabel(exam)}
              </Button>
            </Box>
            );
          })}
        </Box>
      )}

      <ArchiveExamSearch
        theme={theme}
        frameId={frameId}
        frameLabel={frameMeta.label}
        startingId={startingId}
        onStart={handleStart}
      />
    </Box>
  );
};

export default ExamsScreen;
